import { Observable } from 'rxjs';
import { MpxProgramResponse, MpxSeasonContentResponse } from './data/mpx-program-response';
import { MpxSeries } from './data/mpx-series';
import { CacheStorage } from './util/caching';

class CacheAccessor<T> {
  private readonly cache: CacheStorage<T> = new CacheStorage<T>();

  constructor(private readonly cacheName: string) {}

  public has(key: string): boolean {
    return this.cache.hasEntry(key);
  }

  public prepare$(key: string): Observable<T> {
    return this.cache.prepareEntry$(key);
  }

  public get$(key: string): Observable<T> {
    if (!this.has(key)) {
      throw Error(`[${this.cacheName}]: key [${key}] is not stored in cache`);
    }

    return this.cache.get$(key);
  }

  public setCachedValue$(key: string, value: T): Observable<T> {
    return this.cache.setCachedValue$(key, value);
  }

  public bulkSetCachedValueIfNotSetOrNotCached(entries: [key: string, value: T][]) {
    for (const entry of entries) {
      const [key, value] = entry;

      if (!this.has(key)) {
        this.prepare$(key);
      }
      if (!this.cache.isEntryCompleted(key)) {
        this.setCachedValue$(key, value);
      }
    }
  }

  public getCacheSnapshot(): Record<string, T> {
    return this.cache.getCacheSnapshot();
  }

  public restoreFromCacheSnapshot(snapshot: Record<string, T>) {
    this.cache.restoreFromCacheSnapshot(snapshot);
  }
}

export type MpxCacheSnapshot = Record<string, Record<string, any>>;

export class MpxCache {
  // Primary program storage, use this if possible
  private readonly programGuidCache = new CacheStorage<MpxProgramResponse>();
  // Secondary program storage, use this for CW and MyList
  private readonly programIdCache = new CacheStorage<MpxProgramResponse>();

  private readonly feedCache = new CacheStorage<MpxProgramResponse[]>();
  private readonly feedGuidCache = new CacheStorage<string[]>();
  private readonly castGuidCache = new CacheStorage<string[]>();
  private readonly genreGuidCache = new CacheStorage<string[]>();
  private readonly seriesCache = new CacheStorage<MpxSeries>();
  private readonly arabicProgramGuidCache = new CacheStorage<MpxProgramResponse | undefined>();
  public readonly seasonGuidToSeriesCache = new CacheAccessor<MpxSeries>('Season GUID to series');
  public readonly seasonGuidToSeasonCache = new CacheAccessor<MpxSeasonContentResponse>('Season GUID to season');

  constructor() {}

  public getCacheSnapshot(): MpxCacheSnapshot {
    const ret: MpxCacheSnapshot = {};

    Object.keys(this)
      .filter((key) => this[key] instanceof CacheStorage || this[key] instanceof CacheAccessor)
      .forEach((key) => {
        const cacheStorageOrAccessor: CacheStorage<any> | CacheAccessor<any> = this[key];
        ret[key] = cacheStorageOrAccessor.getCacheSnapshot();
      });

    return ret;
  }

  public restoreFromCacheSnapshot(snapshot: MpxCacheSnapshot) {
    Object.keys(this)
      .filter((key) => this[key] instanceof CacheStorage || this[key] instanceof CacheAccessor)
      .forEach((key) => {
        const cacheStorageOrAccessor: CacheStorage<any> | CacheAccessor<any> = this[key];
        cacheStorageOrAccessor.restoreFromCacheSnapshot(snapshot[key] ?? {});
      });
  }

  public hasProgram(programGuid: string): boolean {
    return this.programGuidCache.hasEntry(programGuid);
  }

  public hasArabicProgram(programGuid: string): boolean {
    return this.arabicProgramGuidCache.hasEntry(programGuid);
  }

  public hasProgramById(id: string): boolean {
    return this.programIdCache.hasEntry(id);
  }

  public hasFeed(feedId: string): boolean {
    return this.feedCache.hasEntry(feedId);
  }

  public hasFeedGuids(feedId: string): boolean {
    return this.feedGuidCache.hasEntry(feedId);
  }

  public hasCastGuids(castId: string): boolean {
    return this.castGuidCache.hasEntry(castId);
  }

  public hasGenreGuids(genreGuid: string): boolean {
    return this.genreGuidCache.hasEntry(genreGuid);
  }

  public hasSeries(guid: string): boolean {
    return this.seriesCache.hasEntry(guid);
  }

  public prepareFeedCache$(feedId: string): Observable<MpxProgramResponse[]> {
    return this.feedCache.prepareEntry$(feedId);
  }

  public prepareProgramCache$(guid: string): Observable<MpxProgramResponse> {
    return this.programGuidCache.prepareEntry$(guid);
  }

  public prepareArabicProgramCache$(guid: string): Observable<MpxProgramResponse> {
    return this.arabicProgramGuidCache.prepareEntry$(guid);
  }

  public prepareProgramByIdCache$(id: string): Observable<MpxProgramResponse> {
    return this.programIdCache.prepareEntry$(id);
  }

  public prepareFeedGuids$(feedId: string): Observable<string[]> {
    return this.feedGuidCache.prepareEntry$(feedId);
  }

  public prepareCastGuids$(castId: string): Observable<string[]> {
    return this.castGuidCache.prepareEntry$(castId);
  }

  public prepareGenreGuids$(genreGuid: string): Observable<string[]> {
    return this.genreGuidCache.prepareEntry$(genreGuid);
  }

  public prepareSeriesCache$(guid: string): Observable<MpxSeries> {
    return this.seriesCache.prepareEntry$(guid);
  }

  public cacheFeedContentIds(feedId: string, contentIds: string[]) {
    this.feedGuidCache.setCachedValue$(feedId, contentIds);
  }

  public cacheSeries(guid: string, series: MpxSeries) {
    this.seriesCache.setCachedValue$(guid, series);
  }

  public cacheFeed(feedId: string, feedContent: MpxProgramResponse[]) {
    this.feedCache.setCachedValue$(feedId, feedContent);
    this.cacheProgramValues(feedContent);
  }

  public cacheProgramById(id: string, program: MpxProgramResponse) {
    this.programIdCache.setCachedValue$(id, program);
  }

  public cacheCastGuids(castId: string, guids: string[]) {
    this.castGuidCache.setCachedValue$(castId, guids);
  }

  public cacheGenreGuids(genreGuid: string, guids: string[]) {
    this.genreGuidCache.setCachedValue$(genreGuid, guids);
  }

  public hasCompletedProgramEntryById(id: string): boolean {
    return this.programIdCache.isEntryCompleted(id);
  }

  private cacheProgramIfNotStored(guid: string, program: MpxProgramResponse) {
    if (!this.programGuidCache.hasEntry(guid)) {
      this.programGuidCache.addEntryWithValue$(guid, program);
    } else if (!this.programGuidCache.isEntryCompleted(guid)) {
      this.programGuidCache.setCachedValue$(guid, program);
    }
  }

  public cacheArabicProgramIfNotStored(programGuid: string, program: MpxProgramResponse | undefined) {
    if (!this.arabicProgramGuidCache.hasEntry(programGuid)) {
      this.arabicProgramGuidCache.addEntryWithValue$(programGuid, program);
    } else if (!this.arabicProgramGuidCache.isEntryCompleted(programGuid)) {
      this.arabicProgramGuidCache.setCachedValue$(programGuid, program);
    }
  }

  public cacheProgramValues(programs: MpxProgramResponse[]) {
    for (const program of programs) {
      this.cacheProgramIfNotStored(program.guid, program);
    }
  }

  public setNullProgramCache(guid: string) {
    this.cacheProgramIfNotStored(guid, null);
  }

  public getCachedFeed$(feedId: string): Observable<MpxProgramResponse[]> {
    if (!this.hasFeed(feedId)) {
      throw Error(`Feed ${feedId} is not cached`);
    }

    return this.feedCache.get$(feedId);
  }

  public getCachedProgram$(guid: string): Observable<MpxProgramResponse> {
    if (!this.hasProgram(guid)) {
      throw Error(`Program ${guid} is not cached`);
    }

    return this.programGuidCache.get$(guid);
  }

  public getCachedArabicProgram$(guid: string): Observable<MpxProgramResponse> {
    if (!this.hasArabicProgram(guid)) {
      throw Error(`Arabic program ${guid} is not cached`);
    }

    return this.arabicProgramGuidCache.get$(guid);
  }

  public getCachedProgramById$(id: string): Observable<MpxProgramResponse> {
    if (!this.hasProgramById(id)) {
      throw Error(`Program ${id} is not cached`);
    }

    return this.programIdCache.get$(id);
  }

  public getCachedSeries$(guid: string): Observable<MpxSeries> {
    if (!this.hasSeries(guid)) {
      throw Error(`Series ${guid} is not cached`);
    }

    return this.seriesCache.get$(guid);
  }

  public getCachedCastGuids$(castId: string): Observable<string[]> {
    if (!this.hasCastGuids(castId)) {
      throw Error(`Cast ${castId} GUIDS are not cached`);
    }

    return this.castGuidCache.get$(castId);
  }

  public getCachedGenreGuids$(genreGuid: string): Observable<string[]> {
    if (!this.hasGenreGuids(genreGuid)) {
      throw Error(`Genre ${genreGuid} GUIDS are not cached`);
    }

    return this.genreGuidCache.get$(genreGuid);
  }
}
